import { clampLevel, glowAlpha, glowRadius } from './panelElements';

/**
 * The 2D room's glow layer.
 *
 * Each lit pixel gets a soft radial halo whose radius AND alpha grow with its level, so at a glance you
 * can see what is on without reading a single number. Drawing 2,904 radial gradients per frame is far
 * too slow, so the halos are PRE-RENDERED once per (level bucket, radius bucket) into small offscreen
 * canvases and then blitted with `globalCompositeOperation = 'lighter'`, which is what makes neighbouring
 * bright pixels bloom into each other. Off pixels are skipped entirely — they draw no glow at all.
 */
const LEVEL_BUCKETS = 16;

export interface GlowSprite {
  canvas: HTMLCanvasElement;
  /** Half-extent in CSS pixels: blit at (x - radius, y - radius). */
  radius: number;
}

export function levelBucket(level: number, buckets = LEVEL_BUCKETS): number {
  const value = clampLevel(level);
  if (value <= 0.002) return 0;
  return Math.max(1, Math.min(buckets, Math.ceil(value * buckets)));
}

/** The one DOM dependency, injectable so the bucketing logic is testable without a browser. */
export type CanvasFactory = () => HTMLCanvasElement;

export class GlowSpriteCache {
  private readonly sprites = new Map<string, GlowSprite>();
  private tint = '255, 231, 184';

  constructor(private readonly createCanvas: CanvasFactory = () => document.createElement('canvas')) {}

  /** The warm-white the tape profile emits; a coloured rig passes its own tint. */
  setTint(rgb: string): void {
    if (rgb === this.tint) return;
    this.tint = rgb;
    this.sprites.clear();
  }

  /**
   * The sprite for a level at the current core radius, or undefined when the pixel is dark.
   * `coreRadius` is bucketed to a tenth of a pixel so zooming does not thrash the cache.
   */
  spriteFor(level: number, coreRadius: number): GlowSprite | undefined {
    const bucket = levelBucket(level);
    if (!bucket) return undefined;
    const core = Math.max(0.6, Math.round(coreRadius * 10) / 10);
    const key = `${bucket}:${core}`;
    const cached = this.sprites.get(key);
    if (cached) return cached;
    const bucketLevel = bucket / LEVEL_BUCKETS;
    const radius = Math.max(1, glowRadius(bucketLevel, core));
    const alpha = glowAlpha(bucketLevel);
    const size = Math.ceil(radius * 2) + 2;
    const canvas = this.createCanvas();
    canvas.width = size;
    canvas.height = size;
    const context = canvas.getContext('2d');
    if (!context) return undefined;
    const centre = size / 2;
    const gradient = context.createRadialGradient(centre, centre, 0, centre, centre, radius);
    gradient.addColorStop(0, `rgba(${this.tint}, ${alpha.toFixed(3)})`);
    gradient.addColorStop(.45, `rgba(${this.tint}, ${(alpha * .38).toFixed(3)})`);
    gradient.addColorStop(1, `rgba(${this.tint}, 0)`);
    context.fillStyle = gradient;
    context.beginPath();
    context.arc(centre, centre, radius, 0, Math.PI * 2);
    context.fill();
    const sprite: GlowSprite = { canvas, radius: centre };
    this.sprites.set(key, sprite);
    return sprite;
  }

  clear(): void {
    this.sprites.clear();
  }

  get size(): number {
    return this.sprites.size;
  }
}
